import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const LOADING_MESSAGES = [
  'Aligning your stars...',
  'Consulting the cosmos...',
  'Reading the celestial map...',
  'Tuning into your frequency...',
  'Awakening your Oracle...',
];

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    const message = LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];

    return (
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexDirection: 'column', background: '#0a0a0f',
        zIndex: 9999,
      }}>
        {/* Animated background */}
        <div style={{
          position: 'absolute', inset: 0, overflow: 'hidden', opacity: 0.4,
          background: `
            radial-gradient(ellipse at 20% 50%, rgba(201,168,76,0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 50%, rgba(100,149,237,0.06) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 100%, rgba(201,168,76,0.04) 0%, transparent 40%)
          `,
        }} />

        {/* Star particles */}
        <style>{`
          @keyframes zeal-float {
            0%, 100% { transform: translateY(0px) scale(1); opacity: 0.3; }
            50% { transform: translateY(-20px) scale(1.5); opacity: 0.8; }
          }
          @keyframes zeal-orbit {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes zeal-breathe {
            0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(201,168,76,0.15); }
            50% { transform: scale(1.08); box-shadow: 0 0 60px rgba(201,168,76,0.3); }
          }
          @keyframes zeal-fadein {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Orbiting ring */}
        <div style={{
          position: 'relative', width: '140px', height: '140px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            position: 'absolute', inset: '-10px',
            border: '1px solid rgba(201,168,76,0.1)',
            borderTopColor: 'rgba(201,168,76,0.4)',
            borderRadius: '50%',
            animation: 'zeal-orbit 3s linear infinite',
          }} />
          <div style={{
            position: 'absolute', inset: '-25px',
            border: '1px solid rgba(201,168,76,0.05)',
            borderBottomColor: 'rgba(201,168,76,0.2)',
            borderRadius: '50%',
            animation: 'zeal-orbit 5s linear infinite reverse',
          }} />

          {/* App icon */}
          <img
            src={`${process.env.PUBLIC_URL}/app-icon.jpg`}
            alt="Zeal Genesis"
            style={{
              width: '90px', height: '90px', borderRadius: '50%',
              border: '2px solid rgba(201,168,76,0.3)',
              animation: 'zeal-breathe 2.5s ease-in-out infinite',
              position: 'relative', zIndex: 1,
            }}
          />
        </div>

        {/* Brand name */}
        <p style={{
          marginTop: '28px', fontFamily: "Cinzel, Georgia, serif",
          fontSize: '20px', color: '#c9a84c', letterSpacing: '3px',
          fontWeight: 500, animation: 'zeal-fadein 0.8s ease-out',
        }}>
          ZEAL GENESIS
        </p>

        {/* Loading message */}
        <p style={{
          marginTop: '12px', fontSize: '13px',
          color: 'rgba(255,255,255,0.35)', letterSpacing: '1px',
          animation: 'zeal-fadein 1.2s ease-out',
        }}>
          {message}
        </p>

        {/* Subtle loading bar */}
        <div style={{
          marginTop: '24px', width: '120px', height: '2px',
          background: 'rgba(201,168,76,0.1)', borderRadius: '1px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: '40%', height: '100%',
            background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)',
            animation: 'zeal-orbit 1.5s ease-in-out infinite',
          }} />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
